import { logger } from '@forest/logger';
import { CookieConsentBanner } from '@ophelos/ui/components/cookie-consent-banner';
import { COOKIE_CONSENT_COPY, type Language } from '@ophelos/ui/components/legal/cookie-consent-copy';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { DEFAULT_COOKIE_CONSENT, useCookieConsent } from '@ophelos/shared/hooks/use-cookie-consent';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/customer-auth-context';

interface CookieConsentContextValue {
  /** Reopen the banner in manage mode (persistent "Cookie settings" entry). */
  openSettings: () => void;
  /** Whether the fixed bottom banner is currently showing (so pages can reserve space for it). */
  bannerOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

const CONFIG_SCOPE = import.meta.env.VITE_CONFIG_SCOPE as string | undefined;
const CONFIG_SCOPE_ID = import.meta.env.VITE_CONFIG_SCOPE_ID as string | undefined;
const CORE_API_URL = import.meta.env.VITE_CORE_API_URL as string | undefined;

// Per-operator deploy: the operator id is the deploy scope, so it is known
// client-side for the audit receipt even before the visitor authenticates.
const OPERATOR_ID = CONFIG_SCOPE === 'operator' ? CONFIG_SCOPE_ID : undefined;

function toCopyLanguage(code: string): Language {
  if (code.startsWith('pt')) return 'pt';
  if (code.startsWith('fr')) return 'fr';
  return 'en';
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const { isPreviewMode, isDemoMode } = useConfiguration();
  const { currentLanguage } = useLanguage();
  const { accountId } = useAuth();

  // The access code authenticates the customer-keyed consent receipt so the
  // server attributes it to this account (never a client-supplied ref).
  const getAccessCode = useCallback(() => localStorage.getItem(STORAGE_KEYS.ACCESS_CODE), []);

  // Never prompt inside the Hub's iframe preview or demo mode.
  const config = useMemo(
    () => ({ ...DEFAULT_COOKIE_CONSENT, enabled: !(isPreviewMode || isDemoMode) }),
    [isPreviewMode, isDemoMode]
  );

  const consent = useCookieConsent({
    config,
    sourceApp: 'customer-portal',
    operatorId: OPERATOR_ID,
    coreApiBaseUrl: CORE_API_URL,
    // Key the receipt to the authenticated account so a send-time consent
    // lookup can find it; anonymous pre-login as before.
    authenticatedSubject: accountId ? { ref: accountId, kind: 'customer' } : undefined,
    getAccessCode,
  });

  const copy = COOKIE_CONSENT_COPY[toCopyLanguage(currentLanguage)];
  const categories = useMemo(
    () =>
      consent.categories.map((c) => {
        const localized = copy.categories.find((x) => x.key === c.key);
        return { ...c, label: localized?.title ?? c.label, description: localized?.body };
      }),
    [consent.categories, copy]
  );

  // Reflect the analytics grant into Datadog RUM live (grant starts collection,
  // revoke stops it). Gated on validity, so an expired or superseded-policy grant
  // stops tracking here even before the visitor re-consents.
  useEffect(() => {
    logger.setTrackingConsent(consent.analyticsGranted);
  }, [consent.analyticsGranted]);

  const value = useMemo<CookieConsentContextValue>(
    () => ({ openSettings: consent.reopen, bannerOpen: consent.open }),
    [consent.reopen, consent.open]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieConsentBanner
        open={consent.open}
        mode={consent.mode}
        categories={categories}
        // Fall back to the in-app policy page when no operator policy URL is configured.
        policyUrl={consent.policyUrl ?? '/cookie-policy'}
        intro={copy.intro}
        ui={copy.ui}
        declineNote={copy.declineNote}
        initialConsent={consent.consent}
        onAcceptAll={consent.acceptAll}
        onRejectAll={consent.rejectAll}
        onSave={consent.save}
        onOpenChange={consent.onOpenChange}
      />
    </CookieConsentContext.Provider>
  );
}

/** Access the persistent "Cookie settings" entry point. Returns null outside the provider. */
export function useCookieConsentSettings(): CookieConsentContextValue | null {
  return useContext(CookieConsentContext);
}
